import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { BattleStyles, BattleCatalogContext, DrawStrip, useSlotImage, LiveDot, Initial, MomentumBar } from './BattleParts';
import {
  C, SIDE, MEDAL, FORMAT_META, useBattleFeed, useServerClock, rollSchedule, slotLabel, fmtMulti, multiColor, fmtAmount,
  roundLabel, sidesOf, sideScore, focusedMatch, versusScore, prizeShares, totalRounds, fightTerms,
  EXIT_COMMAND, isThirdPlace, matchLabel, playersInPlay, setBattleApi, battleApi, fightLabels, winTier,
} from './battleLib';
import { demoState } from './battleDemo';

// ============================================
// SLOT BATTLES — OBS browser source (route: /widget/battles), built natively
// at 800x450 and scaled to whatever 16:9 size the source is (1600x900 gives
// crisp text). Every state renders in the same box so the source never jumps.
// Follows the admin console: lobby → draws → fixtures / new-slot window →
// the duel on stream → verdict → podium. Between battles a stand-by screen
// cycles the formats and the all-time records; before the first feed read
// lands (or while the backend is unreachable) it shows "connecting".
//
// Drama is client-side and keyed on server timestamps: a draw strip only
// runs for a match drawn seconds ago, a bonus only "lands" for a result
// entered just now, the verdict only for a match decided just now — so a
// refreshed source shows the current state instead of replaying the night.
// `?demo=<state>` renders mocks for positioning (battleDemo.js).
// ============================================

const W = 800;
const H = 450;
const TOP = 48;
const RESULT_FRESH_MS = 8000;
const DECIDED_FRESH_MS = 12000;
const FINISHED_SHOW_MS = 10 * 60 * 1000;
// A decided match stays up this long, then the widget goes back to the
// overview (fixtures / lobby) on its own — no click between battles.
const RESULT_LINGER_MS = 25000;

const wait = (ms) => new Promise((r) => setTimeout(r, ms));
const px = (n) => `${n}px`;
const ellipsis = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
const disp = { fontFamily: C.display, fontWeight: 800, letterSpacing: '-0.3px' };
const tag = { fontFamily: C.display, fontSize: 10, fontWeight: 700, letterSpacing: '0.4px', textTransform: 'uppercase', color: C.lav };

// ---------- shell ----------
function Stage({ children }) {
  const [scale, setScale] = useState(1);
  useLayoutEffect(() => {
    const fit = () => setScale(Math.min(window.innerWidth / W, window.innerHeight / H));
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, []);
  return (
    <div style={{ width: W, height: H, transform: `scale(${scale})`, transformOrigin: '0 0', position: 'relative', fontFamily: C.font }}>
      {children}
    </div>
  );
}

const prizeList = (prizes) => (prizes || []).filter((p) => p > 0);

// The case: one rounded panel on near-black with a violet aura. The status
// strip runs along the top — the mode on the left, the prizes (gold, it's
// money) and the live state on the right.
function Frame({ battle, mode, status, prizes, children, frameRef }) {
  const paid = prizeList(prizes).slice(0, 3);
  const medal = ['🥇', '🥈', '🥉'];
  return (
    <div style={{ position: 'absolute', inset: 0, animation: 'sb-fade 0.35s ease both' }}>
      <div ref={frameRef} style={{ position: 'absolute', inset: 0, overflow: 'hidden', borderRadius: 24, background: C.bg, border: '1px solid rgba(168,85,247,0.35)', boxShadow: 'inset 0 0 60px -20px rgba(168,85,247,0.35)' }}>
        <div style={{ position: 'absolute', left: '50%', top: -220, width: 760, height: 420, marginLeft: -380, borderRadius: '50%', background: 'radial-gradient(closest-side, rgba(168,85,247,0.28), transparent)', animation: 'sb-aura 6s ease-in-out infinite', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 140, background: 'linear-gradient(0deg, rgba(124,58,237,0.12), transparent)', pointerEvents: 'none' }} />
        <header style={{ position: 'relative', height: TOP, display: 'flex', alignItems: 'center', gap: 10, padding: '0 18px' }}>
          <span style={{ ...disp, fontSize: 14, fontWeight: 900, background: C.headline, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>SLOT BATTLES</span>
          {mode && (
            <span style={{ ...tag, color: '#fff', borderRadius: 999, padding: '3px 10px', background: 'rgba(168,85,247,0.22)', border: '1px solid rgba(168,85,247,0.5)' }}>
              {mode}
            </span>
          )}
          {battle?.title && <span style={{ ...ellipsis, minWidth: 0, fontSize: 13, fontWeight: 600, color: C.muted }}>{battle.title}</span>}
          <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
            {paid.map((p, i) => (
              <span key={i} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 14, fontWeight: 800, color: C.gold, fontVariantNumeric: 'tabular-nums' }}>
                <span style={{ fontSize: 13 }}>{medal[i]}</span>{fmtAmount(p)}
              </span>
            ))}
            {status}
          </span>
        </header>
        <div style={{ position: 'absolute', left: 18, right: 18, top: TOP - 1, height: 1, background: 'linear-gradient(90deg, transparent, rgba(168,85,247,0.5), transparent)' }} />
        <div style={{ position: 'relative', height: H - TOP }}>{children}</div>
      </div>
    </div>
  );
}

function Status({ children, live = false, muted = false }) {
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 6, ...tag, fontSize: 10, color: muted ? C.muted : '#fff' }}>
      {live && <LiveDot size={7} />}{children}
    </span>
  );
}

// Card art, sized for the widget. `locked` is the face-down card the
// new-slot window shows; an unmatched pick gets a text card instead.
function Art({ slot, w, locked = false, style }) {
  const [broken, setBroken] = useState(false);
  const url = useSlotImage(slot);
  const box = { width: w, height: Math.round((w * 4) / 3), borderRadius: Math.max(6, w * 0.1), flexShrink: 0, boxSizing: 'border-box', ...style };
  if (locked) {
    return (
      <div style={{ ...box, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px dashed rgba(168,85,247,0.55)', background: 'radial-gradient(circle at 50% 40%, rgba(168,85,247,0.2), rgba(0,0,0,0.6))' }}>
        <span style={{ fontSize: w * 0.34, opacity: 0.85 }}>🔒</span>
      </div>
    );
  }
  if (!url || broken) {
    return (
      <div style={{ ...box, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: 6, overflow: 'hidden', border: '1px solid rgba(168,85,247,0.35)', background: 'linear-gradient(170deg, rgba(168,85,247,0.3), rgba(5,5,7,0.95))' }}>
        <span style={{ fontWeight: 700, lineHeight: 1.15, color: C.lavLight, fontSize: Math.max(9, w * 0.11) }}>{w >= 50 ? (slotLabel(slot) || 'no slot') : '🎰'}</span>
      </div>
    );
  }
  return <img src={url} alt="" onError={() => setBroken(true)} style={{ ...box, objectFit: 'cover', display: 'block', boxShadow: '0 12px 26px -14px rgba(0,0,0,0.95), 0 0 0 1px rgba(255,255,255,0.08)' }} />;
}

// ---------- effects ----------
// Gold coins (it's a payout) bursting up from a point and raining down.
function Coins({ x, y, count = 26, rain = false, delay = 0 }) {
  const [pieces] = useState(() => Array.from({ length: count }, (_, i) => {
    const a = -Math.PI / 2 + (Math.random() - 0.5) * 1.6;
    const v = 120 + Math.random() * 160;
    return {
      i,
      x0: rain ? Math.random() * W : x,
      y0: rain ? -30 - Math.random() * 80 : y,
      dx: rain ? (Math.random() - 0.5) * 80 : Math.cos(a) * v * 0.9,
      dy: rain ? H + 80 : 260 + Math.random() * 120,
      s: 10 + Math.random() * 8,
      spin: `${(Math.random() > 0.5 ? 1 : -1) * (540 + Math.random() * 540)}deg`,
      d: rain ? 2.4 + Math.random() * 1.6 : 1.4 + Math.random() * 0.6,
      dl: delay + (rain ? Math.random() * 1.2 : Math.random() * 0.15),
    };
  }));
  return pieces.map((p) => (
    <span
      key={p.i}
      style={{
        position: 'absolute', left: p.x0, top: p.y0, width: p.s, height: p.s, borderRadius: '50%', pointerEvents: 'none', opacity: 0,
        background: `radial-gradient(circle at 35% 30%, #fff7d6, ${C.gold} 45%, ${C.goldDeep})`, boxShadow: '0 0 8px rgba(251,191,36,0.6)',
        '--dx': px(p.dx), '--dy': px(p.dy), '--spin': p.spin,
        animation: `sb-coin ${p.d}s cubic-bezier(.3,.1,.6,1) ${p.dl}s both`,
      }}
    />
  ));
}

// Small violet glints thrown off a plate when its bonus lands.
function Glints({ x, y, color = C.violetHi, count = 12 }) {
  const [pieces] = useState(() => Array.from({ length: count }, (_, i) => {
    const a = Math.random() * Math.PI * 2;
    const v = 40 + Math.random() * 70;
    return { i, dx: Math.cos(a) * v, dy: Math.sin(a) * v, s: 3 + Math.random() * 4 };
  }));
  return pieces.map((p) => (
    <span
      key={p.i}
      style={{
        position: 'absolute', left: x, top: y, width: p.s, height: p.s, transform: 'rotate(45deg)', background: color, boxShadow: `0 0 10px ${color}`, pointerEvents: 'none',
        '--dx': px(p.dx), '--dy': px(p.dy), animation: 'sb-glint 0.7s ease-out both',
      }}
    />
  ));
}

function useCountUp(target, animate, ms = 1200) {
  const [v, setV] = useState(animate ? 0 : target);
  const first = useRef(true);
  useEffect(() => {
    const go = animate && first.current;
    first.current = false;
    if (target == null) { setV(null); return undefined; }
    if (!go && !animate) { setV(target); return undefined; }
    let raf;
    const t0 = performance.now();
    const tick = (t) => {
      const k = Math.min(1, (t - t0) / ms);
      setV(target * (1 - (1 - k) ** 4));
      if (k < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, animate, ms]);
  return v;
}

// A ring that fills as the lineup grows.
function Ring({ value, of, size = 132, children }) {
  const r = size / 2 - 9;
  const len = 2 * Math.PI * r;
  const share = of ? Math.min(1, value / of) : 1;
  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }} aria-hidden>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(168,85,247,0.16)" strokeWidth="9" />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={C.violet} strokeWidth="9" strokeLinecap="round" strokeDasharray={`${len * share} ${len}`} style={{ transition: 'stroke-dasharray 700ms ease', filter: 'drop-shadow(0 0 6px rgba(168,85,247,0.8))' }} />
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>{children}</div>
    </div>
  );
}

// ---------- lobby ----------
// A chat line with the command, the lineup count in a ring, and the latest
// picks scrolling along the bottom.
function Lobby({ state }) {
  const { battle, entries, entry_count: count } = state;
  const feed = entries.slice(0, 14);
  const loop = feed.length ? [...feed, ...feed] : [];
  const blurb = battle.format === 'tournament'
    ? `${battle.bracket_size}-seat knockout cup · seats drawn live · best multi moves on`
    : fightTerms(battle) || FORMAT_META[battle.format].tag;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 28, padding: '0 34px' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <span style={{ ...tag, fontSize: 11, display: 'flex', alignItems: 'center', gap: 8, color: battle.entries_open ? C.lav : C.dim }}>
            {battle.entries_open ? <><LiveDot size={7} /> entries open — type in twitch chat</> : '🔒 entries locked'}
          </span>
          <div style={{ marginTop: 14, display: 'inline-flex', alignItems: 'center', gap: 12, maxWidth: '100%', borderRadius: '22px 22px 22px 6px', padding: '14px 22px', background: 'rgba(168,85,247,0.1)', border: '1px solid rgba(168,85,247,0.5)', boxShadow: '0 0 40px -14px rgba(168,85,247,0.9)', boxSizing: 'border-box' }}>
            <span style={{ fontSize: 15, fontWeight: 800, color: C.violetHi }}>you:</span>
            <span style={{ ...disp, ...ellipsis, fontSize: battle.command.length > 7 ? 34 : 42, color: '#fff' }}>{battle.command}</span>
            <span style={{ ...disp, ...ellipsis, fontSize: 24, fontWeight: 600, color: C.lav }}>your slot</span>
          </div>
          <p style={{ margin: '14px 0 0', fontSize: 15, fontWeight: 600, color: C.bright }}>{blurb}</p>
          {battle.entries_open && (
            <p style={{ margin: '6px 0 0', fontSize: 12.5, color: C.muted }}>
              Send it again to swap slots · <strong style={{ color: '#fff' }}>{EXIT_COMMAND}</strong> to drop out
            </p>
          )}
        </div>
        <Ring value={count} of={battle.format === 'tournament' ? battle.bracket_size : null}>
          <span key={count} style={{ ...disp, fontSize: 38, color: '#fff', lineHeight: 1, animation: 'sb-count 0.5s ease-out' }}>{count}</span>
          <span style={{ ...tag, fontSize: 9, marginTop: 5 }}>{battle.format === 'tournament' ? `of ${battle.bracket_size}` : 'in'}</span>
        </Ring>
      </div>
      {/* the latest picks, as a ticker */}
      <div style={{ position: 'relative', height: 74, borderTop: '1px solid rgba(168,85,247,0.2)', background: 'rgba(168,85,247,0.05)', overflow: 'hidden' }}>
        {loop.length ? (
          <div style={{ display: 'flex', gap: 12, height: '100%', alignItems: 'center', width: 'max-content', animation: `sb-ticker ${Math.max(18, feed.length * 4)}s linear infinite`, paddingLeft: 12 }}>
            {loop.map((e, i) => (
              <div key={`${e.id}:${i}`} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '6px 14px 6px 6px', borderRadius: 14, background: 'rgba(5,5,7,0.7)', border: '1px solid rgba(255,255,255,0.07)' }}>
                <Art slot={e.slot} w={34} />
                <div>
                  <div style={{ fontSize: 14, fontWeight: 800, color: '#fff', whiteSpace: 'nowrap' }}>{e.name}</div>
                  <div style={{ fontSize: 11.5, color: e.slot?.name ? C.lav : C.dim, whiteSpace: 'nowrap', fontStyle: e.slot?.name ? 'normal' : 'italic' }}>{e.slot?.name || `“${e.slot?.raw}”`}</div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, color: C.muted }}>First pick lands here…</div>
        )}
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', background: `linear-gradient(90deg, ${C.bg}, transparent 12%, transparent 88%, ${C.bg})` }} />
      </div>
    </div>
  );
}

// ---------- tournament: fixtures ----------
// The whole bracket isn't readable at this size, so between matches the
// widget shows the current round as a list of fixtures under a round stepper.
function Stepper({ rounds, round }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      {Array.from({ length: rounds }, (_, i) => i + 1).map((r) => {
        const short = r === rounds ? 'F' : r === rounds - 1 ? 'SF' : r === rounds - 2 ? 'QF' : `R${2 ** (rounds - r + 1)}`;
        const on = r === round;
        const past = r < round;
        return (
          <React.Fragment key={r}>
            {r > 1 && <span style={{ width: 22, height: 2, background: past || on ? C.violet : 'rgba(168,85,247,0.2)' }} />}
            <span style={{
              ...tag, fontSize: 9.5, minWidth: 30, height: 24, padding: '0 6px', borderRadius: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box',
              ...(on ? { background: C.violet, color: '#fff', boxShadow: '0 0 14px rgba(168,85,247,0.8)' } : past ? { background: 'rgba(168,85,247,0.25)', color: C.lavLight } : { border: '1px solid rgba(168,85,247,0.3)', color: C.dim }),
            }}>{short}</span>
          </React.Fragment>
        );
      })}
    </div>
  );
}

function Fixture({ m, n, battle, compact }) {
  const { a, b } = sidesOf(m);
  const done = m.status === 'done';
  const focus = m.id === battle.focus_match_id && !done;
  const seat = (p, side) => {
    if (!p) return <span style={{ flex: 1, fontSize: 12, color: C.faint, textAlign: side === 'b' ? 'right' : 'left' }}>TBD</span>;
    const won = done && m.winner_side === side;
    const lost = done && m.winner_side !== side;
    return (
      <span style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 6, flexDirection: side === 'b' ? 'row-reverse' : 'row', opacity: lost ? 0.42 : 1 }}>
        {!compact && <Art slot={p.slot} w={22} style={{ borderRadius: 4 }} />}
        <span style={{ ...ellipsis, minWidth: 0, fontSize: compact ? 12.5 : 14, fontWeight: won ? 800 : 600, color: '#fff' }}>{won && '👑 '}{p.name}</span>
        {p.multi != null && <span style={{ ...disp, fontSize: compact ? 11 : 12.5, color: multiColor(p.multi), flexShrink: 0 }}>{fmtMulti(p.multi)}</span>}
      </span>
    );
  };
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8, borderRadius: 12, padding: compact ? '0 8px' : '0 10px', minHeight: 0, boxSizing: 'border-box',
      border: `1px solid ${focus ? C.violet : 'rgba(255,255,255,0.06)'}`, background: focus ? 'rgba(168,85,247,0.16)' : 'rgba(255,255,255,0.025)',
      animation: 'sb-rise 0.4s both',
    }}>
      <span style={{ ...tag, fontSize: 9, width: 24, color: focus ? '#fff' : C.dim, flexShrink: 0 }}>{isThirdPlace(m, battle) ? '3RD' : `M${n}`}</span>
      {m.plays.length ? <>{seat(a[0], 'a')}<span style={{ color: C.violet, fontSize: 10, flexShrink: 0 }}>◆</span>{seat(b[0], 'b')}</> : (
        <span style={{ flex: 1, textAlign: 'center', fontSize: 12, color: C.faint }}>seat not drawn yet</span>
      )}
    </div>
  );
}

function Fixtures({ state }) {
  const { battle, matches } = state;
  const round = battle.current_round || 1;
  const list = matches.filter((m) => m.round === round)
    .sort((x, y) => Number(isThirdPlace(y, battle)) - Number(isThirdPlace(x, battle)) || x.idx - y.idx);
  const n = list.length || battle.bracket_size / 2;
  const cols = n > 8 ? 2 : 1;
  const compact = n > 8;
  const drawn = list.filter((m) => m.plays.length > 0).length;
  const played = list.filter((m) => m.status === 'done').length;
  const pooling = battle.entries_open && drawn < n;
  const withThird = list.some((m) => isThirdPlace(m, battle));
  return (
    <div style={{ display: 'flex', height: '100%', flexDirection: 'column', padding: '10px 22px 16px', boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
        <div>
          <div style={{ ...disp, fontSize: 24, color: '#fff' }}>{withThird ? 'Bronze match & Final' : roundLabel(round, battle.bracket_size)}</div>
          <span style={{ fontSize: 12.5, color: C.muted }}>{pooling ? `${drawn} of ${n} matches drawn` : `${played} of ${n} played`}</span>
        </div>
        <Stepper rounds={totalRounds(battle.bracket_size)} round={round} />
      </div>
      <div style={{ marginTop: 12, display: 'grid', flex: 1, minHeight: 0, gap: 6, gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, gridAutoRows: 'minmax(0, 1fr)', gridAutoFlow: 'column', gridTemplateRows: `repeat(${Math.ceil(n / cols)}, minmax(0, 1fr))` }}>
        {list.map((m, i) => <Fixture key={m.id} m={m} n={i + 1 - (withThird ? 1 : 0)} battle={battle} compact={compact} />)}
      </div>
      {pooling && (
        <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontSize: 13, color: C.bright }}>
          <LiveDot size={7} /> Not drawn yet? <strong style={{ color: '#fff' }}>{battle.command} your slot</strong> still gets you in · {Math.max(0, state.entry_count - state.players.length)} waiting
        </div>
      )}
    </div>
  );
}

// ---------- tournament: new-slot window ----------
function SlotWindow({ state }) {
  const { battle } = state;
  const round = battle.picking_round;
  const alive = playersInPlay(state);
  const picked = alive.filter((p) => (p.picked_round ?? 0) >= round).length;
  const n = alive.length;
  const cols = n <= 8 ? 2 : n <= 16 ? 4 : 8;
  const art = n <= 4 ? 52 : n <= 8 ? 36 : n <= 16 ? 24 : 0;
  return (
    <div style={{ display: 'flex', height: '100%', gap: 22, padding: '14px 24px 18px', boxSizing: 'border-box' }}>
      <div style={{ width: 210, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
        <Ring value={picked} of={n} size={150}>
          <span key={picked} style={{ ...disp, fontSize: 40, color: '#fff', lineHeight: 1, animation: 'sb-count 0.5s ease-out' }}>{picked}<span style={{ color: C.dim, fontSize: 22 }}>/{n}</span></span>
          <span style={{ ...tag, fontSize: 9, marginTop: 5 }}>locked in</span>
        </Ring>
        <div style={{ ...disp, marginTop: 14, fontSize: 18, color: '#fff' }}>New slots, please</div>
        <p style={{ margin: '6px 0 0', fontSize: 12.5, color: C.muted, lineHeight: 1.4 }}>
          {battle.third_place && round === totalRounds(battle.bracket_size) ? 'Bronze match & Final' : roundLabel(round, battle.bracket_size)} — still alive? Send <strong style={{ color: '#fff' }}>{battle.command} your slot</strong>
        </p>
      </div>
      <div style={{ flex: 1, minWidth: 0, display: 'grid', alignContent: 'center', gap: 8, gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
        {alive.map((p) => {
          const done = (p.picked_round ?? 0) >= round;
          return (
            <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, borderRadius: 12, padding: art ? 6 : '6px 8px', border: `1px solid ${done ? 'rgba(168,85,247,0.55)' : 'rgba(255,255,255,0.06)'}`, background: done ? 'rgba(168,85,247,0.14)' : 'rgba(255,255,255,0.02)', transition: 'background 400ms, border-color 400ms' }}>
              {art > 0 && (done ? <div key="s" style={{ animation: 'sb-pop 0.5s both' }}><Art slot={p.slot} w={art} /></div> : <Art locked w={art} />)}
              <div style={{ minWidth: 0 }}>
                <div style={{ ...ellipsis, fontSize: n > 16 ? 11 : 13.5, fontWeight: 800, color: '#fff' }}>{!art && (done ? '✓ ' : '… ')}{p.name}</div>
                {n <= 16 && <div style={{ ...ellipsis, fontSize: 11, color: done ? C.lav : C.dim }}>{done ? slotLabel(p.slot) : 'choosing…'}</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------- the duel ----------
const STRIP_W = { 1: 92, 2: 46, 3: 38 };
function StripCard({ entry, w }) {
  return (
    <div style={{ borderRadius: 10, padding: 4, background: 'rgba(0,0,0,0.5)', border: '1px solid rgba(168,85,247,0.3)' }}>
      <Art slot={entry.slot} w={w - 8} />
      {w >= 60 && <div style={{ ...ellipsis, marginTop: 4, fontSize: 11, fontWeight: 700, color: '#fff', textAlign: 'center' }}>{entry.name}</div>}
    </div>
  );
}

// One bonus buy: a draw strip while the draw runs, then the player.
function PlaySlot({ play, solo, spin, total, clock, pool, accent, teamSize }) {
  const [landed, setLanded] = useState(!spin);
  useEffect(() => {
    if (landed) return undefined;
    const t = setTimeout(() => setLanded(true), spin * 1000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const fresh = clock(play.result_at) < RESULT_FRESH_MS;
  const multi = useCountUp(play.multi, fresh);
  const sw = STRIP_W[teamSize] || 40;
  if (!landed) {
    return (
      <DrawStrip pool={pool} target={{ name: play.name, slot: play.slot }} spin={spin} total={total} seed={play.id} accent={accent}
        cardW={sw} height={solo ? 168 : teamSize === 2 ? 80 : 66} gap={solo ? 10 : 6} renderCard={(e) => <StripCard entry={e} w={sw} />} />
    );
  }
  const multiEl = play.buy != null ? (
    <span key={play.result_at} style={{ ...disp, display: 'inline-block', color: multiColor(play.multi), fontVariantNumeric: 'tabular-nums', textShadow: play.multi >= 100 ? '0 0 22px rgba(74,222,128,0.6)' : 'none', animation: fresh ? 'sb-pop 0.6s both' : undefined }}>
      {fmtMulti(multi)}
    </span>
  ) : (
    <span style={{ ...tag, fontSize: solo ? 11 : 9, color: C.dim, animation: 'sb-pulse 2s ease-in-out infinite' }}>awaiting bonus</span>
  );
  if (solo) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', animation: spin ? 'sb-pop 0.55s both' : undefined }}>
        <Art slot={play.slot} w={92} />
        <div style={{ ...disp, ...ellipsis, marginTop: 10, maxWidth: '100%', fontSize: play.name.length > 12 ? 17 : 21, color: '#fff' }}>
          {play.name}{play.is_streamer && <span style={{ ...tag, fontSize: 9, color: '#fff', background: C.violet, borderRadius: 999, padding: '2px 7px', marginLeft: 6, verticalAlign: 'middle' }}>host</span>}
        </div>
        <div style={{ ...ellipsis, maxWidth: '100%', fontSize: 13, color: play.slot?.name ? C.bright : C.muted, fontStyle: play.slot?.name ? 'normal' : 'italic' }}>{play.slot?.name || (play.slot?.raw ? `“${play.slot.raw}”` : 'no slot')}</div>
        <div style={{ marginTop: 6, fontSize: 34, lineHeight: 1.1, minHeight: 38 }}>{multiEl}</div>
        {play.buy != null && <div style={{ fontSize: 12, color: C.dim, fontVariantNumeric: 'tabular-nums' }}>{fmtAmount(play.buy)} → <span style={{ color: C.gold }}>{fmtAmount(play.payout)}</span></div>}
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 9, animation: spin ? 'sb-pop 0.5s both' : undefined }}>
      <Art slot={play.slot} w={teamSize === 2 ? 40 : 32} />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ ...ellipsis, fontSize: teamSize === 2 ? 15 : 13.5, fontWeight: 800, color: '#fff' }}>{play.name}{play.is_streamer && ' 🎙️'}</div>
        <div style={{ ...ellipsis, fontSize: 11.5, color: play.slot?.name ? C.muted : C.dim }}>{play.slot?.name || play.slot?.raw || 'no slot'}</div>
      </div>
      <div style={{ fontSize: teamSize === 2 ? 20 : 17, flexShrink: 0 }}>{multiEl}</div>
    </div>
  );
}

const Plate = React.forwardRef(function Plate({ sideKey, plays, label, won, lost, verdict, spins, totals, rolling, clock, pool, score, teamSize }, ref) {
  const accent = SIDE[sideKey];
  const solo = teamSize === 1;
  const showWon = won && verdict;
  const showLost = lost && verdict;
  return (
    <div
      ref={ref}
      style={{
        position: 'relative', flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', borderRadius: 20, padding: solo ? '12px 14px' : '10px 12px', boxSizing: 'border-box',
        background: showWon ? 'linear-gradient(180deg, rgba(168,85,247,0.3), rgba(124,58,237,0.08))' : 'rgba(255,255,255,0.03)',
        border: `1px solid ${showWon ? C.violet : rolling ? accent : 'rgba(255,255,255,0.08)'}`,
        boxShadow: showWon ? '0 0 46px -10px rgba(168,85,247,0.95)' : 'none',
        filter: showLost ? 'grayscale(1) brightness(0.6)' : 'none',
        transition: 'background 600ms, border-color 400ms, box-shadow 600ms, filter 800ms',
        animation: rolling ? undefined : `sb-rise 0.5s ${sideKey === 'a' ? 0 : 0.08}s both`,
      }}
    >
      {/* side tab on the plate's top edge */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: solo ? 8 : 6, minWidth: 0 }}>
        <span style={{ width: 9, height: 9, borderRadius: 2, background: accent, transform: 'rotate(45deg)', flexShrink: 0 }} />
        <span style={{ ...tag, ...ellipsis, minWidth: 0, fontSize: 10, color: showWon ? '#fff' : C.lav }}>{rolling ? 'drawing…' : label}</span>
      </div>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: solo ? 'center' : 'flex-start', gap: teamSize === 3 ? 5 : 8 }}>
        {plays.map((p) => (
          <PlaySlot key={p.id} play={p} solo={solo} spin={spins?.[p.id]} total={totals?.[p.id]} clock={clock} pool={pool} accent={accent} teamSize={teamSize} />
        ))}
      </div>
      {!solo && (
        <div style={{ marginTop: 6, paddingTop: 6, borderTop: '1px solid rgba(255,255,255,0.07)', display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
          <span style={{ ...tag, fontSize: 9, color: C.muted }}>squad avg</span>
          <span style={{ ...disp, fontSize: 21, color: multiColor(score) }}>{fmtMulti(score)}</span>
        </div>
      )}
      {showWon && (
        <span style={{ position: 'absolute', top: -20, left: '50%', marginLeft: -16, fontSize: 30, animation: 'sb-crown 0.7s both', filter: 'drop-shadow(0 4px 10px rgba(0,0,0,0.6))' }}>👑</span>
      )}
      {showLost && (
        <span style={{ position: 'absolute', left: '50%', top: '50%', padding: '6px 26px', background: 'repeating-linear-gradient(45deg, #e4e4e7 0 14px, #a1a1aa 14px 28px)', color: '#18181b', ...disp, fontSize: 22, letterSpacing: '3px', boxShadow: '0 8px 24px rgba(0,0,0,0.6)', animation: 'sb-tape 0.45s cubic-bezier(.2,1.3,.4,1) both', whiteSpace: 'nowrap', filter: 'none' }}>
          BUSTED
        </span>
      )}
    </div>
  );
});

function Duel({ state, match, clock, frameRef }) {
  const { battle } = state;
  const { a, b } = sidesOf(match);
  const done = match.status === 'done';
  // fixed at mount: a re-render mid-draw must not re-time the strips
  const [sched] = useState(() => rollSchedule(match, clock(match.rolled_at)));
  // what the strips run through: the lineup as it stood when the draw landed
  const [pool] = useState(() => state.entries.map((e) => ({ name: e.name, slot: e.slot })));
  const [rolling, setRolling] = useState(!!sched);
  const score = { a: done ? match.a_score : sideScore(a), b: done ? match.b_score : sideScore(b) };

  const plateRef = { a: useRef(null), b: useRef(null) };
  const rootRef = useRef(null);
  const [fx, setFx] = useState([]);
  const [verdict, setVerdict] = useState(() => done && clock(match.decided_at) >= DECIDED_FRESH_MS);
  const handled = useRef(new Set());
  const queue = useRef(Promise.resolve());
  const fxId = useRef(0);
  const addFx = useCallback((f, ttl = 1600) => {
    const id = ++fxId.current;
    setFx((list) => [...list, { ...f, id }]);
    setTimeout(() => setFx((list) => list.filter((x) => x.id !== id)), ttl);
  }, []);
  const enqueue = (fn) => { queue.current = queue.current.then(fn).catch(() => {}); };

  // a plate's centre in duel coordinates (the stage is scaled; undo it)
  const centreOf = (side) => {
    const root = rootRef.current;
    const el = plateRef[side].current;
    if (!root || !el) return { x: side === 'a' ? W * 0.27 : W * 0.73, y: 160 };
    const r = root.getBoundingClientRect();
    const k = r.width / root.offsetWidth || 1;
    const t = el.getBoundingClientRect();
    return { x: (t.left - r.left + t.width / 2) / k, y: (t.top - r.top + t.height / 2) / k };
  };

  // A bonus lands: its plate charges with light and throws glints, its multi
  // counts up, and the momentum bar swings. The other plate isn't touched —
  // nothing ever shows on a side that didn't buy.
  const land = async (p) => {
    await wait(150);
    const el = plateRef[p.side].current;
    const glow = p.side === 'a' ? 'rgba(168,85,247,0.85)' : 'rgba(226,232,240,0.7)';
    if (el && el.animate) {
      el.animate([
        { boxShadow: `0 0 0 0 ${glow}`, transform: 'scale(1)' },
        { boxShadow: `0 0 52px 6px ${glow}`, transform: 'scale(1.025)', offset: 0.35 },
        { boxShadow: `0 0 0 0 ${glow}`, transform: 'scale(1)' },
      ], { duration: 900, easing: 'ease-out' });
    }
    const at = centreOf(p.side);
    addFx({ type: 'glints', x: at.x, y: at.y - 30, color: p.multi >= 1 ? (p.side === 'a' ? C.violetHi : '#fff') : C.loss }, 800);
    await wait(900);
  };

  // The verdict: a beat of tension, the tier ("Mega win") lands in the
  // middle, the winner gets the crown and a coin shower, the loser is taped
  // off as BUSTED. Big margins sweep a shine across the whole case.
  const decide = async () => {
    const w = match.winner_side;
    const l = w === 'a' ? 'b' : 'a';
    const tier = winTier(match[`${w}_score`], match[`${l}_score`]);
    await wait(450);
    if (tier) addFx({ type: 'tier', label: tier.label, level: tier.level }, 2400);
    if (tier?.level >= 2 && frameRef.current) addFx({ type: 'shine' }, 1200);
    await wait(500);
    setVerdict(true);
    const at = centreOf(w);
    addFx({ type: 'coins', x: at.x, y: at.y + 40, count: tier?.level >= 2 ? 44 : 28 }, 2600);
  };

  // the draw landing: glints off each plate as its last strip stops
  useEffect(() => {
    if (!sched) return undefined;
    const timers = ['a', 'b'].filter((s) => sched.sides[s] > 0).map((s) => setTimeout(() => {
      const at = centreOf(s);
      addFx({ type: 'glints', x: at.x, y: at.y - 20, color: SIDE[s] }, 800);
    }, sched.sides[s] * 1000));
    timers.push(setTimeout(() => setRolling(false), sched.end * 1000 + 150));
    return () => timers.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    for (const p of match.plays) {
      const k = `r${p.id}:${p.result_at}`;
      if (p.result_at && !handled.current.has(k)) {
        handled.current.add(k);
        if (clock(p.result_at) < RESULT_FRESH_MS) enqueue(() => land(p));
      }
    }
    if (match.status === 'done') {
      const k = `d${match.decided_at}`;
      if (!handled.current.has(k)) {
        handled.current.add(k);
        if (clock(match.decided_at) < DECIDED_FRESH_MS && !match.forfeit) enqueue(decide);
        else enqueue(async () => setVerdict(true));
      }
    } else {
      setVerdict(false); // a result was cleared
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [match]);

  const labels = fightLabels(match, battle);
  let heading;
  let outcome = null;
  if (battle.format === 'tournament') {
    const total = totalRounds(battle.bracket_size);
    const inRound = state.matches.filter((m) => m.round === match.round).length;
    const third = isThirdPlace(match, battle);
    const isFinal = match.round === total && !third;
    heading = third || isFinal ? matchLabel(match, battle) : `${roundLabel(match.round, battle.bracket_size)}${inRound > 1 ? ` · match ${match.idx + 1} of ${inRound}` : ''}`;
    if (done) {
      const wp = match.winner_side === 'a' ? a[0] : b[0];
      outcome = isFinal ? <>👑 {wp?.name} lifts the cup</>
        : third ? <>🥉 {wp?.name} takes bronze</>
          : <>{wp?.name} moves on to the {roundLabel(match.round + 1, battle.bracket_size).toLowerCase()}</>;
    }
  } else if (battle.format === 'versus') {
    const s = versusScore(state);
    heading = `battle ${match.round}${match.team_size > 1 ? ` · ${match.team_size}v${match.team_size}` : ''} · ${battle.streamer_name} ${s.house} : ${s.chat} chat`;
    if (done) {
      const shares = prizeShares(match, battle, match.winner_side);
      const each = match.prize_amount && shares ? (shares > 1 ? Math.floor((match.prize_amount / shares) * 100) / 100 : match.prize_amount) : null;
      outcome = match.winner_side === 'a'
        ? <>{battle.streamer_name} holds the house</>
        : <>Chat beats {battle.streamer_name}{each ? <> · <span style={{ color: C.gold }}>{fmtAmount(each)}{shares > 1 ? ' each' : ''}</span></> : ''}</>;
    }
  } else {
    heading = `arena battle ${match.round} · ${match.team_size}v${match.team_size}`;
    if (done) {
      const each = match.prize_amount ? Math.floor((match.prize_amount / (match.team_size || 1)) * 100) / 100 : null;
      outcome = <>{labels[match.winner_side]} wins{each ? <> · <span style={{ color: C.gold }}>{fmtAmount(each)}{match.team_size > 1 ? ' each' : ''}</span></> : ''}</>;
    }
  }

  const plate = (key) => ({
    sideKey: key, plays: key === 'a' ? a : b, label: labels[key], won: done && match.winner_side === key, lost: done && match.winner_side !== key,
    verdict, spins: sched?.spins, totals: sched?.totals, rolling: rolling && (sched?.sides?.[key] || 0) > 0, clock, pool, score: score[key],
    teamSize: match.team_size,
  });

  return (
    <div ref={rootRef} style={{ position: 'relative', display: 'flex', height: '100%', flexDirection: 'column', padding: '8px 18px 14px', boxSizing: 'border-box' }}>
      <div style={{ height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {verdict && outcome ? (
          <span key="out" style={{ ...disp, fontSize: 16, color: '#fff', animation: 'sb-drop 0.5s both' }}>{outcome}</span>
        ) : rolling ? (
          <span style={{ ...tag, fontSize: 11, display: 'flex', alignItems: 'center', gap: 8 }}><LiveDot size={7} /> drawing from the lineup</span>
        ) : (
          <span style={{ ...tag, fontSize: 11, color: C.bright }}>{heading}</span>
        )}
      </div>
      <div style={{ position: 'relative', marginTop: 10, flex: 1, minHeight: 0, display: 'flex', gap: 16 }}>
        <Plate ref={plateRef.a} {...plate('a')} />
        <Plate ref={plateRef.b} {...plate('b')} />
        {/* the VS medallion between the plates */}
        <div style={{ position: 'absolute', left: '50%', top: '50%', width: 52, height: 52, margin: '-26px 0 0 -26px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: `radial-gradient(circle at 35% 30%, ${C.violetHi}, ${C.violetDeep})`, border: `3px solid ${C.bg}`, boxShadow: '0 0 24px rgba(168,85,247,0.8)', zIndex: 2 }}>
          <span style={{ ...disp, fontSize: 15, color: '#fff' }}>VS</span>
        </div>
      </div>
      <div style={{ marginTop: 12 }}>
        <MomentumBar a={score.a} b={score.b} height={10} showValues fontSize="16px" />
        {match.prize_amount > 0 && !done && (
          <div style={{ marginTop: 6, textAlign: 'center', fontSize: 12.5, color: C.muted }}>
            on the line: <strong style={{ color: C.gold }}>{fmtAmount(match.prize_amount)}</strong>{match.team_size > 1 ? ` · split ${match.team_size} ways` : ''}
          </div>
        )}
      </div>

      {/* effects layer — positions in duel coordinates */}
      <div style={{ position: 'absolute', inset: 0, overflow: 'visible', pointerEvents: 'none' }}>
        {fx.map((f) => {
          if (f.type === 'glints') return <div key={f.id} style={{ position: 'absolute', inset: 0 }}><Glints x={f.x} y={f.y} color={f.color} /></div>;
          if (f.type === 'coins') return <div key={f.id} style={{ position: 'absolute', left: 0, right: 0, top: -TOP, bottom: 0 }}><Coins x={f.x} y={f.y} count={f.count} /></div>;
          if (f.type === 'tier') {
            return (
              <div key={f.id} style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 5 }}>
                <span style={{
                  ...disp, fontWeight: 900, fontSize: f.level >= 2 ? 64 : 50, textTransform: 'uppercase', whiteSpace: 'nowrap',
                  background: f.level >= 3 ? `linear-gradient(135deg, #fff, ${C.lav} 40%, ${C.violet} 75%, #fff)` : C.headline, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                  filter: 'drop-shadow(0 6px 22px rgba(124,58,237,0.9))', animation: 'sb-tier 0.6s cubic-bezier(.2,1.3,.4,1) both',
                }}>
                  {f.label}
                </span>
              </div>
            );
          }
          if (f.type === 'shine') {
            return (
              <div key={f.id} style={{ position: 'absolute', left: 0, right: 0, top: -TOP, bottom: 0, overflow: 'hidden' }}>
                <div style={{ position: 'absolute', top: 0, bottom: 0, width: '30%', background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.18), transparent)', animation: 'sb-shine 1.1s ease-in-out both' }} />
              </div>
            );
          }
          return null;
        })}
      </div>
    </div>
  );
}

// ---------- finish ----------
function PodiumStage({ state, clock }) {
  const top = [2, 1, 3].map((n) => state.players.find((p) => p.placement === n)).filter(Boolean);
  const celebrate = clock(state.battle.finished_at) < 60000;
  const cfg = { 1: { h: 132, art: 70, delay: 0.9 }, 2: { h: 104, art: 54, delay: 0.45 }, 3: { h: 88, art: 54, delay: 0.15 } };
  return (
    <div style={{ position: 'relative', display: 'flex', height: '100%', flexDirection: 'column', alignItems: 'center', padding: '6px 40px 0', boxSizing: 'border-box' }}>
      <span style={{ ...disp, fontSize: 15, color: C.lav }}>The cup is decided</span>
      <div style={{ flex: 1, width: '100%', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', gap: 14 }}>
        {top.map((p) => {
          const c = cfg[p.placement];
          return (
            <div key={p.id} style={{ flex: '1 1 0', maxWidth: 210, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', animation: `sb-rise 0.8s ${c.delay}s both` }}>
              {p.placement === 1 && <span style={{ fontSize: 30, animation: `sb-crown 0.7s ${c.delay + 0.5}s both` }}>👑</span>}
              <Art slot={p.slot} w={c.art} />
              <div style={{ ...disp, ...ellipsis, marginTop: 8, maxWidth: '100%', fontSize: p.placement === 1 ? 19 : 15, color: '#fff' }}>{p.name}</div>
              <div style={{ marginTop: 8, width: '100%', height: c.h, borderRadius: '16px 16px 0 0', display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 10, boxSizing: 'border-box', background: `linear-gradient(180deg, rgba(168,85,247,${p.placement === 1 ? 0.55 : 0.28}), rgba(168,85,247,0.05))`, borderTop: `4px solid ${MEDAL[p.placement]}` }}>
                <span style={{ ...disp, fontSize: 30, fontWeight: 900, color: MEDAL[p.placement], lineHeight: 1 }}>{p.placement}</span>
                {p.prize_amount > 0 && <span style={{ ...disp, marginTop: 4, fontSize: p.placement === 1 ? 20 : 15, color: C.gold }}>{fmtAmount(p.prize_amount)}</span>}
              </div>
            </div>
          );
        })}
      </div>
      {celebrate && (
        <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, top: -TOP, overflow: 'hidden', pointerEvents: 'none' }}>
          <Coins rain count={34} delay={1.2} />
        </div>
      )}
    </div>
  );
}

function SessionSummary({ state }) {
  const { battle, matches } = state;
  const done = matches.filter((m) => m.status === 'done');
  const s = versusScore(state);
  const winners = state.players.filter((p) => p.prize_amount > 0);
  return (
    <div style={{ display: 'flex', height: '100%', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '0 24px 24px', boxSizing: 'border-box' }}>
      <span style={{ ...disp, fontSize: 15, color: C.lav }}>That's a wrap</span>
      {battle.format === 'versus' ? (
        <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 26 }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
            <Initial name={battle.streamer_name} size={54} />
            <span style={{ ...tag, color: '#fff' }}>{battle.streamer_name}</span>
          </div>
          <span style={{ ...disp, fontSize: 84, fontWeight: 900, color: '#fff', fontVariantNumeric: 'tabular-nums' }}>{s.house}<span style={{ color: C.violet }}>:</span>{s.chat}</span>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
            <span style={{ width: 54, height: 54, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, border: `2px solid ${C.silver}`, background: 'rgba(203,213,225,0.12)' }}>💬</span>
            <span style={{ ...tag, color: '#fff' }}>chat</span>
          </div>
        </div>
      ) : (
        <div style={{ ...disp, marginTop: 8, fontSize: 52, fontWeight: 900, background: C.headline, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>{done.length} battle{done.length === 1 ? '' : 's'}</div>
      )}
      {winners.length > 0 && (
        <div style={{ marginTop: 22, display: 'flex', maxWidth: 720, flexWrap: 'wrap', justifyContent: 'center', gap: 8 }}>
          {winners.slice(0, 10).map((p) => (
            <span key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, borderRadius: 14, border: '1px solid rgba(168,85,247,0.3)', background: 'rgba(168,85,247,0.08)', padding: '5px 12px 5px 5px', fontSize: 14 }}>
              <Initial name={p.name} size={24} />
              <span style={{ fontWeight: 700, color: '#fff' }}>{p.name}</span>
              <span style={{ fontWeight: 800, color: C.gold }}>{fmtAmount(p.prize_amount)}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------- stand-by ----------
// Nothing running: the formats one at a time in a spotlight, plus the
// all-time records rotating underneath (from Legends) — so the source never
// looks frozen on stream.
function useRecords(live) {
  const [lines, setLines] = useState([]);
  useEffect(() => {
    if (!live) return undefined;
    let alive = true;
    const load = () => battleApi('/api/battles/leaderboard').then((d) => {
      if (!alive || !d?.totals) return;
      const out = [];
      const m = d.top_multis?.[0];
      if (m) out.push(['💥 Biggest multi ever', `${m.name} · ${fmtMulti(m.multi)}${m.slot?.name ? ` on ${m.slot.name}` : ''}`]);
      const e = d.top_earners?.[0];
      if (e) out.push(['💰 Top earner', `${e.name} · ${fmtAmount(e.total)}`]);
      const c = d.champions?.[0];
      if (c) out.push(['🏆 Most cups', `${c.name} · ${c.titles}`]);
      if (d.house && d.house.house + d.house.chat > 0) out.push(['🎙️ Snutzy vs chat', `${d.house.house} : ${d.house.chat}`]);
      if (d.totals.paid > 0) out.push(['💸 Paid out to chat', fmtAmount(d.totals.paid)]);
      setLines(out);
    }).catch(() => {});
    load();
    const t = setInterval(load, 5 * 60 * 1000);
    return () => { alive = false; clearInterval(t); };
  }, [live]);
  return lines;
}

function Standby({ state, connecting = false, live = true }) {
  const [on, setOn] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setOn((x) => x + 1), 3200);
    return () => clearInterval(t);
  }, []);
  const records = useRecords(live && !connecting);
  const formats = ['tournament', 'arena', 'versus'];
  const f = formats[on % formats.length];
  const rec = records.length ? records[on % records.length] : null;
  const command = state?.battle?.command || '!join';
  return (
    <div style={{ display: 'flex', height: '100%', flexDirection: 'column', alignItems: 'center', padding: '6px 40px 18px', boxSizing: 'border-box', textAlign: 'center' }}>
      <span style={{ ...tag, fontSize: 11, display: 'flex', alignItems: 'center', gap: 8, color: '#fff' }}>
        <LiveDot size={7} /> {connecting ? 'connecting…' : 'next battle soon'}
      </span>
      <div style={{ flex: 1, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ ...disp, fontSize: 52, fontWeight: 900, lineHeight: 1.05, background: C.headline, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>SLOT BATTLES</div>
      {/* the spotlight */}
      <div key={f} style={{ marginTop: 16, width: '100%', display: 'flex', alignItems: 'center', gap: 18, textAlign: 'left', borderRadius: 20, padding: '16px 20px', boxSizing: 'border-box', background: 'linear-gradient(135deg, rgba(168,85,247,0.2), rgba(168,85,247,0.04))', border: '1px solid rgba(168,85,247,0.45)', animation: 'sb-rise 0.5s both' }}>
        <span style={{ fontSize: 44 }}>{FORMAT_META[f].icon}</span>
        <div style={{ minWidth: 0 }}>
          <div style={{ ...disp, fontSize: 21, color: '#fff' }}>{FORMAT_META[f].label} <span style={{ ...tag, fontSize: 10, marginLeft: 6 }}>{FORMAT_META[f].tag}</span></div>
          <div style={{ marginTop: 4, fontSize: 13, color: C.bright, lineHeight: 1.4 }}>{FORMAT_META[f].blurb}</div>
        </div>
      </div>
      <div style={{ marginTop: 10, display: 'flex', gap: 6 }}>
        {formats.map((k) => <span key={k} style={{ width: k === f ? 22 : 7, height: 7, borderRadius: 999, background: k === f ? C.violet : 'rgba(168,85,247,0.3)', transition: 'width 400ms' }} />)}
      </div>
      </div>
      <div style={{ height: 22 }}>
        {rec && (
          <span key={`${on}`} style={{ fontSize: 14, color: C.bright, animation: 'sb-rise 0.5s both' }}>
            <strong style={{ color: '#fff' }}>{rec[0]}</strong> — {rec[1]}
          </span>
        )}
      </div>
      <p style={{ margin: '8px 0 0', fontSize: 14, color: C.muted }}>
        When it opens: <span style={{ ...disp, fontSize: 15, color: '#fff' }}>{command}</span> <span style={{ color: C.lav }}>your slot</span> in chat
      </p>
    </div>
  );
}

// ---------- routing ----------
function pickView(state, clock) {
  const b = state?.battle;
  if (!b) return { view: 'standby' };
  if (b.status === 'finished') {
    if (clock(b.finished_at) > FINISHED_SHOW_MS) return { view: 'standby' };
    // the final's verdict plays out before the podium takes over
    const last = state.matches.slice().sort((x, y) => Date.parse(y.decided_at || 0) - Date.parse(x.decided_at || 0))[0];
    if (b.format === 'tournament' && last && clock(last.decided_at) < 9000) return { view: 'duel', match: last };
    return { view: b.format === 'tournament' ? 'podium' : 'summary' };
  }
  if (b.status === 'lobby') return { view: 'lobby' };
  if (b.status !== 'live') return { view: 'standby' };
  let focus = focusedMatch(state);
  if (focus?.status === 'done' && clock(focus.decided_at) > RESULT_LINGER_MS) focus = null;
  if (b.format === 'tournament') {
    if (b.picking_round && !(focus && focus.round < b.picking_round && clock(focus.decided_at) < 9000)) return { view: 'picking' };
    if (focus) return { view: 'duel', match: focus };
    return { view: 'fixtures' };
  }
  if (focus) return { view: 'duel', match: focus };
  return { view: 'lobby' };
}

// Ticks while a time-based switch is pending (verdict → podium, etc.), so
// the view changes on schedule even when no new state arrives.
function useTick(ms) {
  const [, set] = useState(0);
  useEffect(() => {
    const t = setInterval(() => set((n) => n + 1), ms);
    return () => clearInterval(t);
  }, [ms]);
}

function statusFor(state, v) {
  if (v.view === 'standby') return <Status muted>{v.connecting ? 'connecting' : 'stand-by'}</Status>;
  const b = state.battle;
  if (v.view === 'lobby') return <Status live={b.entries_open}>{b.entries_open ? 'entries open' : 'locked'}</Status>;
  if (v.view === 'picking') return <Status live>new slots</Status>;
  if (v.view === 'podium') return <Status>final standings</Status>;
  if (v.view === 'summary') return <Status muted>finished</Status>;
  if (v.view === 'fixtures') {
    if (b.entries_open) return <Status live>drawing seats</Status>;
    const alive = state.players.filter((p) => !p.replaced && p.eliminated_round == null).length;
    return <Status muted>{alive} alive</Status>;
  }
  return <Status live={v.match.status === 'pending'}>{v.match.status === 'pending' ? 'live' : 'result'}</Status>;
}

function Widget({ state, live }) {
  const clock = useServerClock(state);
  useTick(1000);
  const frameRef = useRef(null);
  // undefined = the first feed read hasn't landed (or the backend is down):
  // the stand-by screen, marked connecting.
  const v = state === undefined ? { view: 'standby', connecting: true } : pickView(state, clock);
  const key = v.view === 'standby' ? 'standby' : `${state.battle.id}:${v.view}:${v.match?.id || ''}`;
  const b = state?.battle;
  // a tournament's prizes stay up top while it's played — not in the lobby
  // (it shows them big) or the podium (that shows what was won)
  const prizes = b?.format === 'tournament' && ['fixtures', 'picking', 'duel'].includes(v.view) ? b.prizes : null;
  const mode = v.view === 'standby' ? null : FORMAT_META[b.format].label;
  return (
    <Stage>
      <Frame key={key} battle={v.view === 'standby' ? null : b} mode={mode} status={statusFor(state, v)} prizes={prizes} frameRef={frameRef}>
        {v.view === 'standby' && <Standby state={state} connecting={v.connecting} live={live} />}
        {v.view === 'lobby' && <Lobby state={state} />}
        {v.view === 'fixtures' && <Fixtures state={state} />}
        {v.view === 'picking' && <SlotWindow state={state} />}
        {v.view === 'duel' && <Duel key={v.match.id} state={state} match={v.match} clock={clock} frameRef={frameRef} />}
        {v.view === 'podium' && <PodiumStage state={state} clock={clock} />}
        {v.view === 'summary' && <SessionSummary state={state} />}
      </Frame>
    </Stage>
  );
}

function Live() {
  const { state } = useBattleFeed();
  return <Widget state={state} live />;
}

// Mock states for positioning; each one replays its animation on a loop
// (the champion demo runs long enough to reach the podium).
function Demo({ name }) {
  const [n, setN] = useState(0);
  const [state, setState] = useState(() => demoState(name));
  useEffect(() => {
    const t = setInterval(() => { setState(demoState(name)); setN((x) => x + 1); }, name === 'final-result' ? 18000 : 9000);
    return () => clearInterval(t);
  }, [name]);
  return <Widget key={n} state={state} live={false} />;
}

export default function BattleWidget({ apiUrl, slotCatalog = [] }) {
  setBattleApi(apiUrl);
  // Transparent page for OBS
  useEffect(() => {
    document.body.style.background = 'transparent';
    document.documentElement.style.background = 'transparent';
    document.body.style.overflow = 'hidden';
  }, []);
  const [demo] = useState(() => {
    try { return new URLSearchParams(window.location.search).get('demo'); } catch { return null; }
  });
  return (
    <BattleCatalogContext.Provider value={slotCatalog}>
      <BattleStyles />
      {demo ? <Demo name={demo === '1' ? 'lobby' : demo} /> : <Live />}
    </BattleCatalogContext.Provider>
  );
}
